import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, RotateCw, X, RefreshCcw, AlertTriangle } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DeploymentStatusBadge, DeploymentTypeBadge } from "./DeploymentBadges";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}

function dur(seconds) {
  if (seconds == null) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// Timeline colors per status — reused
const TL = {
  PENDING: "bg-slate-500",
  QUEUED: "bg-sky-500",
  RUNNING: "bg-amber-500",
  SUCCESS: "bg-emerald-500",
  FAILED: "bg-rose-500",
  CANCELLED: "bg-neutral-500",
};

function Timeline({ logs = [] }) {
  if (!logs.length) {
    return <div className="text-xs text-white/40 italic">Belum ada aktivitas.</div>;
  }
  return (
    <ol className="relative border-l border-white/10 ml-3 space-y-4" data-testid="deployment-timeline">
      {logs.map((log, idx) => (
        <li key={idx} className="ml-4">
          <div className={`absolute -left-[7px] w-3 h-3 rounded-full ${TL[log.status] || "bg-white/20"}`}
            data-testid={`timeline-dot-${idx}`} />
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-wider text-white/70">{log.status}</span>
            <span className="text-[10px] text-white/40 font-mono">{fmt(log.at)}</span>
          </div>
          <div className="text-xs text-white/80 mt-0.5" data-testid={`timeline-message-${idx}`}>{log.message}</div>
        </li>
      ))}
    </ol>
  );
}

function InfoRow({ label, value, testid, mono = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div className="text-[11px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-sm text-white font-semibold ${mono ? "font-mono" : ""}`} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

export default function DeploymentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dep, setDep] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/deployments/${id}`);
      setDep(r.data);
      if (r.data?.tenant_id) {
        try {
          const t = await api.get(`/platform/tenants/${r.data.tenant_id}`);
          setTenant(t.data);
        } catch { /* tenant lookup optional */ }
      }
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/platform/deployments/${id}/${verb}`, body);
      toast.success(`Deployment ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  if (loading || !dep) {
    return (
      <PlatformLayout title="Deployment Detail">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      </PlatformLayout>
    );
  }

  const canStart = dep.deployment_status === "PENDING";
  const canRetry = dep.deployment_status === "FAILED";
  const canCancel = ["PENDING", "QUEUED", "RUNNING"].includes(dep.deployment_status);

  return (
    <PlatformLayout title={dep.deployment_number}>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" onClick={() => nav("/platform/deployments")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dep-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dep-refresh-btn">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          {canStart && (
            <Button onClick={() => act("start", { simulate: "success" })} disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="dep-detail-start">
              <Play size={14} className="mr-1" /> Start (Success)
            </Button>
          )}
          {canStart && (
            <Button onClick={() => act("start", { simulate: "fail", error_message: "simulated failure" })} disabled={busy}
              className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="dep-detail-start-fail">
              <Play size={14} className="mr-1" /> Start (Fail sim)
            </Button>
          )}
          {canRetry && (
            <Button onClick={() => act("retry")} disabled={busy}
              className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="dep-detail-retry">
              <RotateCw size={14} className="mr-1" /> Retry
            </Button>
          )}
          {canCancel && (
            <Button onClick={() => {
              const reason = window.prompt("Alasan cancel?") || "";
              act("cancel", { reason });
            }} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="dep-detail-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {dep.deployment_status === "FAILED" && dep.error_message && (
        <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="deployment-error-message">
          <AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-rose-200 font-black text-sm">Deployment Gagal</div>
            <div className="text-xs text-rose-100/80 mt-1 font-mono break-words whitespace-pre-wrap">{" "}{dep.error_message}</div>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-white font-display font-black text-lg" data-testid="dep-detail-number">{dep.deployment_number}</div>
            <DeploymentStatusBadge status={dep.deployment_status} />
            <DeploymentTypeBadge type={dep.deployment_type} />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Timeline</div>
          <Timeline logs={dep.logs || []} />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Konteks</div>
          <InfoRow label="Tenant" value={tenant?.tenant_name || dep.tenant_id.slice(0, 8)} testid="dep-info-tenant" />
          <InfoRow label="Tenant Code" value={tenant?.tenant_code || "—"} testid="dep-info-tenant-code" mono />
          <InfoRow label="Plan" value={dep.plan_code} testid="dep-info-plan" />
          <InfoRow label="Template" value={dep.template_id || "—"} testid="dep-info-template" mono />
          <InfoRow label="Environment" value={dep.environment} testid="dep-info-env" />
          <InfoRow label="Version" value={dep.deployment_version} testid="dep-info-version" mono />
          <InfoRow label="Triggered By" value={dep.triggered_by || "—"} testid="dep-info-triggered-by" />
          <InfoRow label="Created" value={fmt(dep.created_at)} testid="dep-info-created" />
          <InfoRow label="Started" value={fmt(dep.started_at)} testid="dep-info-started" />
          <InfoRow label="Finished" value={fmt(dep.finished_at)} testid="dep-info-finished" />
          <InfoRow label="Duration" value={dur(dep.duration_seconds)} testid="dep-info-duration" mono />
          {dep.notes ? (
            <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-2.5">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1">Notes</div>
              <div className="text-xs text-white/80 whitespace-pre-wrap">{dep.notes}</div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/deployments" className="text-xs text-white/50 hover:text-white underline">← Semua deployment</Link>
      </div>
    </PlatformLayout>
  );
}
